import { useCallback, useEffect, useMemo, useState } from "react";
import { EyeIcon } from "@heroicons/react/24/solid";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import ColoredBadge from "../../components/ColoredBadge";
import BulkClientActivityReview from "../../components/client/BulkClientActivityReview";
import ClientReviewBanner from "../../components/client/ClientReviewBanner";
import Header from "../../components/Header";
import DialogConfirmationForRemovingClients from "../../components/Modals/DialogConfirmationForRemovingClients";
import PageContainer from "../../components/PageContainer";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import Sidebar from "../../components/Sidebar";
import { getClientSubscriptionTypeLabel } from "../../helpers/clientSubscriptionTypeLabel";
import { FIRMFLOW_NAMES } from "../../helpers/firmFlowNames";
import { formatPhoneNumber } from "../../helpers/formatPhoneNumber";
import { formatTaxNumber } from "../../helpers/formatTaxNumber";
import { usePermissions } from "../../context/PermissionsContext";
import { useCelebration } from "../../context/CelebrationContext";
import { deleteClient } from "../../services/deleteClient";
import { getClients } from "../../services/getClients";
import { getAnnualClientActivityReviewQueueCount } from "../../services/client/getAnnualClientActivityReviewQueue";

function Clients() {
    const { can } = usePermissions();
    const { celebrate } = useCelebration();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tooltip, showTooltip] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedClient, setSelectedClient] = useState({});
    const [includeInactive, setIncludeInactive] = useState(false);
    const [reviewQueueCount, setReviewQueueCount] = useState(0);
    const [bulkReviewOpen, setBulkReviewOpen] = useState(false);
    const [pendingSessionSummary, setPendingSessionSummary] = useState(null);
    //const [clientSubscriptions, setClientSubscriptions] = useState(null);
    //const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const navigate = useNavigate();
    const noPermissionMessage = "You don't have permission to do this.";
    const canWriteClients = can("clients.write");
    const canDeleteClients = can("clients.delete");
    const canWriteClientServices = can("client_services.write");
    const canDeleteClientServices = can("client_services.delete");

    const refreshReviewQueueCount = useCallback(async () => {
        if (!canWriteClientServices) {
            setReviewQueueCount(0);
            return;
        }

        const response = await getAnnualClientActivityReviewQueueCount();
        if (!response?.success) return;
        const data = response?.data;
        const count = Number(
            typeof data === "number"
                ? data
                : data?.totalCount ?? data?.TotalCount ?? data?.count ?? data?.Count ?? 0,
        );
        setReviewQueueCount(Number.isFinite(count) && count > 0 ? count : 0);
    }, [canWriteClientServices]);

    const callOnLoad = async () => {
        setLoading(true);
        const response = await getClients({ includeInactive });
        console.log(response);

        if (!response?.success) {
            setClients([]);
            setLoading(false);

            // 403 toast is handled globally by axios interceptor.
            if (response?.status !== 403) {
                toast.error(response?.message || "Failed to load clients.");
            }
            return;
        }

        const source = Array.isArray(response?.data) ? response.data : [];
        const list = source.map((client, i) => ({
            ...client,
            index: i,
            checked: false,
        }));

        setClients(list);
        setLoading(false);
    };

    useEffect(() => {
        callOnLoad();
        //getNewLoggedInEmployeeFunction(setLoggedInEmployee);
    }, [includeInactive]);

    useEffect(() => {
        refreshReviewQueueCount();
    }, [refreshReviewQueueCount]);

    const handleBulkSessionEnd = useCallback(
        ({ changedCount }) => {
            setBulkReviewOpen(false);
            setPendingSessionSummary({ changedCount });
            refreshReviewQueueCount();
        },
        [refreshReviewQueueCount],
    );

    const handleBulkDialogAfterLeave = useCallback(() => {
        const changedCount = pendingSessionSummary?.changedCount ?? 0;
        if (changedCount > 0) {
            toast.success(
                `Nice work — ${changedCount} ${
                    changedCount === 1 ? "client was" : "clients were"
                } updated.`,
            );
            celebrate();
        }
        setPendingSessionSummary(null);
    }, [celebrate, pendingSessionSummary]);

    const removeClient = async (client) => {
        /*   if (loggedInEmployee.role === 0) {
      toast.error("You don't have permission to do this.");
      return;
    } */
        console.log("deleting client...", client.clientName);
        const res = await deleteClient(client.id);
        console.log(res);
        if (res.success) {
            toast.success("Client deleted successfully");
            callOnLoad();
        } else {
            if (String(res.message || "").includes("403")) return;
            toast.error(res.message);
        }
    };

    const capitalizeFirstLetter = (text) => {
        if (!text) return "";
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    };

    /* const fetchClientSubscriptions = async (clientId) => {
        const res = await getClientSubscriptionsByClientId(clientId);
        console.log(res);
        if (res.success) {
            setClientSubscriptions(res.data.clientServiceSubscriptions);
        } else {
            toast.error(res.message);
        }
    }; */

    const onClickDelete = (client) => {
        //fetchClientSubscriptions(client.id);
        setSelectedClient(client);
        setShowDialog(true);
    };

    const columns = useMemo(
        () => [
            {
                Header: "Client",
                accessor: "clientName",
                Cell: (props) => (
                    <>
                        {tooltip && (
                            <ReactTooltip
                                type="light"
                                effect="solid"
                                place="top"
                                border={true}
                                borderColor={"rgb(220,220,220)"}
                                className="rounded-md bg-gray-50"
                                multiline={false}
                            />
                        )}
                        <button
                            onMouseEnter={() => showTooltip(true)}
                            onMouseLeave={() => showTooltip(false)}
                            data-tip={`${FIRMFLOW_NAMES.CLIENT_SERVICE_SUBSCRIPTIONS} for ${props.row.original.clientName}`}
                            onClick={() =>
                                navigate(`/clients/manage/${props.row.original.id}`)
                            }
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.row.original.clientName}
                        </button>
                    </>
                ),
            },
            {
                Header: "Tax ID Number",
                accessor: "taxNumber",
                Cell: (props) => (
                    <a
                        className="rounded-full text-gray hover:text-blue-500"
                        href={`/clients/manage/${props.row.original.id}`}
                    >
                        {formatTaxNumber({
                            clientType:
                                props.row.original.clientDetail?.clientType,
                            taxNumber: props.value,
                        })}
                    </a>
                ),
            },
            {
                Header: "Client Type",
                accessor: "clientDetail.clientType",
                Cell: (props) => (
                    <a
                        className="rounded-full text-gray hover:text-blue-500"
                        href={`/clients/manage/${props.row.original.id}`}
                    >
                        {capitalizeFirstLetter(props.value) ?? "Not assigned"}
                    </a>
                ),
            },
            {
                Header: "Email",
                accessor: "clientDetail.clientEmail",
                Cell: (props) => (
                    <a
                        className="rounded-full text-gray hover:text-blue-500"
                        href={`/clients/manage/${props.row.original.id}`}
                    >
                        {props.value || "—"}
                    </a>
                ),
            },
            {
                Header: "Phone",
                accessor: "phoneNumber",
                Cell: (props) => (
                    <a
                        className="rounded-full text-gray hover:text-blue-500"
                        href={`/clients/manage/${props.row.original.id}`}
                    >
                        {formatPhoneNumber(props.value)}
                    </a>
                ),
            },
            {
                Header: "Subscription Type",
                accessor: "subscriptionType",
                Cell: (props) => (
                    <a
                        className="rounded-full text-gray hover:text-blue-500"
                        href={`/clients/manage/${props.row.original.id}`}
                    >
                        {getClientSubscriptionTypeLabel(props.value) ?? "—"}
                    </a>
                ),
            },
            {
                Header: "Is Active?",
                accessor: "isActive",
                Cell: (props) => (
                    <a
                        className="rounded-full text-gray hover:text-blue-500"
                        href={`/clients/manage/${props.row.original.id}`}
                    >
                        {props.value === true ? (
                            <ColoredBadge
                                colorClass="bg-teal-500 text-white"
                                text="Yes"
                            />
                        ) : props.value == null ? (
                            <ColoredBadge
                                colorClass="bg-gray-500 text-white"
                                text="Not assigned"
                            />
                        ) : (
                            <ColoredBadge
                                colorClass="bg-red-500 text-white"
                                text="No"
                            />
                        )}
                    </a>
                ),
            },
            {
                Header: "Actions",
                accessor: "id",
                Cell: (props) => (
                    <>
                        {tooltip && (
                            <ReactTooltip
                                type="light"
                                effect="solid"
                                place="top"
                                border={true}
                                borderColor={"rgb(220,220,220)"}
                                className="rounded-md bg-gray-50"
                                multiline={false}
                            />
                        )}
                        <button
                            onMouseEnter={() => showTooltip(true)}
                            onMouseLeave={() => showTooltip(false)}
                            data-tip={`${FIRMFLOW_NAMES.CLIENT_SERVICE_SUBSCRIPTIONS} for ${props.row.original.clientName}`}
                            onClick={() =>
                                navigate(`/clients/manage/${props.value}`)
                            }
                            className="mx-4 rounded-full text-slate-400 hover:text-slate-500"
                        >
                            <span className="sr-only">Menu</span>
                            <EyeIcon className="w-5 h-5" />
                        </button>
                        <span
                            data-tip={
                                canWriteClients
                                    ? "Edit Basic Info"
                                    : noPermissionMessage
                            }
                            className="inline-flex"
                        >
                            <button
                                onClick={() => navigate(`/clients/${props.value}`)}
                                disabled={!canWriteClients}
                                className={`mx-4 rounded-full text-slate-400 ${
                                    canWriteClients
                                        ? "hover:text-slate-500"
                                        : "opacity-50 cursor-not-allowed"
                                }`}
                            >
                                <span className="sr-only">Menu</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="w-6 h-6"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                                    />
                                </svg>
                            </button>
                        </span>
                        <span
                            data-tip={
                                canDeleteClients ? "Delete" : noPermissionMessage
                            }
                            className="inline-flex"
                        >
                            <button
                                onClick={() => onClickDelete(props.row.original)}
                                disabled={!canDeleteClients}
                                className={`${
                                    canDeleteClients
                                        ? "hover:text-red-500"
                                        : "opacity-50 cursor-not-allowed"
                                }`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="p-1 pt-0 mx-4 w-7 h-7 text-slate-400"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </span>
                    </>
                ),
            },
        ],
        [
            navigate,
            tooltip,
            canWriteClients,
            canDeleteClients,
            noPermissionMessage,
            onClickDelete,
        ],
    );
    // const handleSelectedItems = (selectedItems) => {};

    return (
        <div className="flex h-screen overflow-hidden">
            <Toaster />
            <BulkClientActivityReview
                open={bulkReviewOpen}
                canWriteClientServices={canWriteClientServices}
                canDeleteClientServices={canDeleteClientServices}
                onCountRefresh={refreshReviewQueueCount}
                onSessionEnd={handleBulkSessionEnd}
                onAfterLeave={handleBulkDialogAfterLeave}
            />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />
            {/* Modal */}
            {/* <AddSubscriptionModal
          open={subscriptionModalOpen}
          setOpen={setSubscriptionModalOpen}
          services={services}
          setServices={setServices}
          clients={clientsForSubscription}
          refreshSubs={() => fetchClientSubscriptions()}
        /> */}
            {/* Sidebar */}
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

            {/* Content area */}
            <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                {/*  Site header */}
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <DialogConfirmationForRemovingClients
                    showDialog={showDialog}
                    onCancel={() => {
                        setShowDialog(false);
                        setSelectedClient({});
                    }}
                    onConfirm={() => {
                        removeClient(selectedClient);
                        setShowDialog(false);
                    }}
                    title="Delete Client"
                    description={`Click confirm to delete ${selectedClient.clientName}`}
                    clientSubscriptions={null}
                />
                <PageContainer isLoading={loading} loadingText="Loading clients...">
                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            {/* Page header */}
                            <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                {/* Left: Title */}
                                <div className="mb-4 sm:mb-0">
                                    <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                        Clients
                                    </h1>
                                </div>

                                {/* Right: Actions */}
                                <div className="grid justify-start grid-flow-col gap-4 sm:auto-cols-max sm:justify-end">
                                    <label
                                        htmlFor="includeInactiveClients"
                                        className="flex items-center text-sm font-medium text-slate-600"
                                    >
                                        <input
                                            id="includeInactiveClients"
                                            type="checkbox"
                                            className="mr-2 form-checkbox text-teal-500"
                                            checked={includeInactive}
                                            onChange={(event) =>
                                                setIncludeInactive(event.target.checked)
                                            }
                                        />
                                        Include inactive clients
                                    </label>

                                    {/* Add customer button */}
                                    <span
                                        data-tip={
                                            canWriteClients
                                                ? "Add Client"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            onClick={() =>
                                                navigate("/clients/create")
                                            }
                                            disabled={!canWriteClients}
                                            className={`h-10 text-white bg-teal-500 btn ${
                                                canWriteClients
                                                    ? "hover:bg-teal-600"
                                                    : "opacity-50 cursor-not-allowed"
                                            }`}
                                        >
                                            <svg
                                                className="w-4 h-4 opacity-50 fill-current shrink-0"
                                                viewBox="0 0 16 16"
                                            >
                                                <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                            </svg>
                                            <span className="ml-2 xs:block">
                                                Add Client
                                            </span>
                                        </button>
                                    </span>
                                </div>
                            </div>

                            <ClientReviewBanner
                                count={reviewQueueCount}
                                onClick={() => setBulkReviewOpen(true)}
                            />

                            {/* Table */}
                            <FirmFlowTable data={clients} columns={columns} />
                        </div>
                    </main>
                </PageContainer>
            </div>
        </div>
    );
}

export default Clients;
